import type { ReactElement } from 'react';
import type { Severity } from '@kirin/api-client/contracts/shared/severity';
import { SeverityBadge } from '@/components/policylist/components/SeverityBadge';

interface SkillRiskCellProps {
  /** Null until AgentMesh scores the skill (Phase 2). */
  riskLevel: Severity | null;
}

/**
 * Risk column for Inventory → Skills.
 *
 * Phase 1 always renders the unscored placeholder: nothing populates
 * `riskLevel` until AgentMesh lands, and the column ships visible so the
 * admin knows the signal is coming.
 *
 * Deliberately NOT the extensions' `RiskEstimateCell`, despite the identical
 * placeholder: that one takes an `ExtensionRiskEstimate` of free-text
 * AgentMesh vocabulary and routes it through a best-effort guess map. A
 * skill's `riskLevel` is already the closed `Severity` enum `SeverityBadge`
 * speaks natively, so adapting would mean fabricating an estimate object to
 * get a string back — and the two would only diverge once Phase 2 starts
 * filling the field, in a different PR.
 */
export function SkillRiskCell({ riskLevel }: SkillRiskCellProps): ReactElement {
  if (riskLevel === null) {
    return (
      <span className="inline-flex items-center gap-1.5 text-muted-foreground">
        <span
          aria-hidden
          className="size-1.5 shrink-0 rounded-full bg-muted-foreground/50"
        />
        Coming soon
      </span>
    );
  }

  return <SeverityBadge riskLevel={riskLevel} showIcon={false} />;
}

SkillRiskCell.displayName = 'SkillRiskCell';
